/*
 * cornhole-configurator.js
 * Gedeelde engine voor de custom-configurator (cornhole-skin voor het WW-thema).
 * Superset van de BLNC-engine: + zakjes-contributor + data-cart-mode.
 * Geladen als <script type="module">.
 */

/* ---------- Pure prijsfunctie (getest) ---------- */
function computeTotal(state) {
  var total = (state && state.variantPrice) || 0;
  var c = (state && state.choices) || {};
  Object.keys(c).forEach(function (k) { if (c[k] && c[k].price) total += c[k].price; });
  ((state && state.upsells) || []).forEach(function (u) { if (u && u.price) total += u.price; });
  ((state && state.zakjes) || []).forEach(function (z) { if (z && z.price) total += z.price; });
  if (state && state.express && state.express.price) total += state.express.price;
  return total;
}

/* ---------- Meest gekozen zakjes-categorie (getest) ---------- */
/*
 * Bepaalt welke zakjes-categorie het "meest gekozen"-label krijgt, op basis van
 * de boarddikte. Die dikte staat bij de meeste sets in de variant-titel
 * ("10mm", "15mm COMP") en bij sets met één variant in de producttitel
 * ("SURF - Cornhole Set - 12mm"); vandaar dat beide teksten worden meegegeven.
 *
 * Regel (besluit Tjarco 31-7-2026, aangescherpt 8-8-2026):
 *   COMP / ELITE -> Pro
 *   15mm / 18mm  -> Premium, anders Comp, anders Stick & Slide
 *   12mm         -> Premium, anders Stick & Slide, anders Comp
 *   10mm         -> Stick & Slide (double sided), anders Premium, anders Comp
 *   geen dikte   -> Premium, anders Stick & Slide, anders Comp
 *
 * COMP wint van de dikte: "15mm COMP" is een pro-set, geen premium-set.
 *
 * REC staat bewust in geen enkele terugvalketen. "REC standaard is nooit meest
 * gekozen" (Tjarco 8-8-2026) -- het is de instapzak die standaard al bij de set
 * zit, dus hem aanwijzen als meest gekozen beveelt aan wat de klant sowieso
 * krijgt en verkoopt niets. Custom evenmin: dat vraagt een upload en is geen
 * aanbeveling die je iemand ongevraagd voorlegt.
 *
 * Comp mag pas vanaf 15mm meedoen. Bij dunnere boards is dat een zwaardere zak
 * dan het board aankan.
 *
 * Sets met eigen design hebben een eigen ladder (Tjarco 8-8-2026, eigenDesign):
 *   15mm -> Comp, 12mm -> Stick & Slide (double sided).
 * Daar staat de hele dikteladder als variant op één pagina ("12mm REC" /
 * "15mm REC" / "15mm COMP"), dus loopt de zakjeskeuze mee met de boardkwaliteit
 * in plaats van drie keer op Premium uit te komen. Gewone sets houden Premium.
 *
 * Levert de keten niets op, dan geeft de functie null en toont er geen badge.
 * Dat is beter dan de vorige terugval "altijd Premium": een badge op een
 * categorie die het product nauwelijks voert is een lege claim.
 */
function bepaalMeestGekozen(tekst, beschikbareCategorieen, eigenDesign) {
  var t = String(tekst || '').toLowerCase();
  var cats = beschikbareCategorieen || [];
  function eerste(keten) {
    for (var i = 0; i < keten.length; i++) {
      if (cats.indexOf(keten[i]) > -1) return keten[i];
    }
    return null;
  }

  // \b voorkomt dat "Complete Cornhole Set" als COMP-set wordt gelezen.
  if (/\bcomp\b/.test(t) || /\belite\b/.test(t)) return eerste(['pro', 'comp', 'premium', 'slide']);
  if (eigenDesign && /\b15\s*mm\b/.test(t)) return eerste(['comp', 'premium', 'slide']);
  if (eigenDesign && /\b12\s*mm\b/.test(t)) return eerste(['slide', 'premium', 'comp']);
  if (/\b(15|18)\s*mm\b/.test(t)) return eerste(['premium', 'comp', 'slide']);
  if (/\b12\s*mm\b/.test(t)) return eerste(['premium', 'slide', 'comp']);
  if (/\b10\s*mm\b/.test(t)) return eerste(['slide', 'premium', 'comp']);
  return eerste(['premium', 'slide', 'comp']);
}

/* ---------- Cart-properties (getest) ---------- */
/*
 * De set en zijn add-ons worden als losse cart-regels toegevoegd, want Picqer pikt
 * line items op SKU en niet op properties. Om ze tóch aan elkaar vast te kunnen
 * knopen draagt elke regel _cfg_role, en draagt de setregel in _cfg_lock_ids het
 * recept: de variant-id's van de verplichte zakjes, mét dubbelingen. Zie
 * docs/superpowers/specs/2026-08-03-cfg-zakjes-cartslot-design.md.
 *
 * ÁLLE keuzelabels op de setregel dragen een onderstreepje. Daarmee zijn ze
 * onzichtbaar in winkelwagen en checkout, maar blijven ze leesbaar in de
 * Shopify-order. Dat is geen cosmetiek maar de kern van de fix: elke keuze is óók
 * een eigen cart-regel, en een line-item-property is na het toevoegen niet meer te
 * wijzigen. Zou de setregel "Express: Ja" tonen, dan blijft die tekst staan nadat de
 * klant de expresregel heeft weggehaald — en dan liegt de regel. Precies dat gebeurde
 * bij order WW54343, waar de set nog "Zakjes 1: Grau" vermeldde zonder zakjes.
 *
 * De losse regel is de enige plek waar een keuze getoond wordt: die kan verdwijnen
 * en dan verdwijnt de mededeling mee.
 */
function bouwHoofdProps(state, uid) {
  var props = {};
  var labels = { print: '_print', painted: '_painted_white', finish: '_afwerking', stoppers: '_stoppers' };
  var c = (state && state.choices) || {};
  Object.keys(c).forEach(function (k) {
    if (c[k] && c[k].id && c[k].label) props[labels[k] || ('_' + k)] = c[k].label;
  });

  var zakjes = (state && state.zakjes) || [];
  zakjes.forEach(function (z, i) { props['_zakjes_' + (i + 1)] = z.label || ''; });
  if (state && state.express) props['_express'] = 'Ja';

  props['_config'] = uid;
  props['_cfg_role'] = 'main';

  var receptIds = zakjes.filter(function (z) { return z.required && z.id; })
                        .map(function (z) { return z.id; });
  if (receptIds.length) props['_cfg_lock_ids'] = receptIds.join(',');

  return props;
}

function bouwAddonItems(state, uid, qty) {
  var items = [];
  var q = qty || 1;
  function regel(id, rol) {
    return { id: id, quantity: q, properties: { _config: uid, _cfg_role: rol } };
  }

  var c = (state && state.choices) || {};
  Object.keys(c).forEach(function (k) { if (c[k] && c[k].id) items.push(regel(c[k].id, 'addon')); });
  // Custom-zakjes worden apart via multipart toegevoegd (eigen Design-bestand) — hier overslaan.
  ((state && state.zakjes) || []).forEach(function (z) {
    if (z.id && z.category !== 'custom') items.push(regel(z.id, z.required ? 'locked' : 'addon'));
  });
  ((state && state.upsells) || []).forEach(function (u) { if (u.id) items.push(regel(u.id, 'addon')); });
  if (state && state.express && state.express.id) items.push(regel(state.express.id, 'addon'));
  return items;
}

/*
 * data-uid is `cfg-<product.id>` en staat dus vast per productpagina. Zou _config
 * die waarde rechtstreeks krijgen, dan delen twee verschillend geconfigureerde sets
 * van hetzelfde product dezelfde groepssleutel — en gooit het groepsslot bij het
 * verwijderen van de ene set ook de zakjes van de andere weg. Daarom per
 * add-to-cart een verse uid.
 */
function maakUid(basis, stempel) {
  var s = (stempel === undefined || stempel === null) ? Date.now() : stempel;
  return String(basis) + '-' + Number(s).toString(36);
}

export { computeTotal, bepaalMeestGekozen, bouwHoofdProps, bouwAddonItems, maakUid };

/* ---------- Browser-controller ---------- */
function formatMoney(cents) {
  var amount = (cents / 100);
  try {
    return new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(amount);
  } catch (e) {
    return '€ ' + amount.toFixed(2).replace('.', ',');
  }
}

function readNum(el, attr) {
  var v = el && el.getAttribute(attr);
  var n = parseInt(v, 10);
  return isNaN(n) ? 0 : n;
}

function Configurator(root) {
  this.root = root;
  this.baseUid = root.getAttribute('data-uid') || String(root.id || 'cfg');
  this.uid = maakUid(this.baseUid);
  this.cartMode = root.getAttribute('data-cart-mode') || 'horizon';
  this.totalEl = root.querySelector('[data-blnc-total]');
  this.atcBtn = root.querySelector('[data-blnc-atc]');
  this.fileInput = root.querySelector('[data-blnc-file]');
  this.qtyInput = root.querySelector('[data-blnc-qty]');
  this.state = { variantId: null, variantPrice: 0, choices: {}, upsells: [], zakjes: [], express: null };
  this.init();
}

Configurator.prototype.init = function () {
  var self = this;

  var selVariant = this.root.querySelector('.blnc-cfg__variant.is-sel') || this.root.querySelector('.blnc-cfg__variant');
  if (selVariant) {
    this.selectVariant(selVariant, false);
  } else {
    // Geen variant-laag (bv. product met 1 variant): pak de basisvariant uit root-data
    this.state.variantId = readNum(this.root, 'data-default-variant-id') || null;
    this.state.variantPrice = readNum(this.root, 'data-default-variant-price');
  }

  this.root.querySelectorAll('.blnc-cfg__choice input[type="radio"]:checked').forEach(function (r) { self.applyChoice(r, false); });

  this.root.querySelectorAll('.blnc-cfg__variant').forEach(function (btn) {
    btn.addEventListener('click', function () {
      if (btn.getAttribute('data-available') === 'false') return;
      self.selectVariant(btn, true);
      self.advanceFrom(btn);
    });
  });

  this.root.querySelectorAll('.blnc-cfg__choice input[type="radio"]').forEach(function (r) {
    r.addEventListener('change', function () { self.applyChoice(r, true); self.advanceFrom(r); });
  });

  this.root.querySelectorAll('.blnc-cfg__addon input[type="checkbox"], .blnc-cfg__express input[type="checkbox"]').forEach(function (cb) {
    cb.addEventListener('change', function () { self.recomputeAddons(); self.render(); });
  });

  // Zakjes: gedelegeerd zodat dynamisch toegevoegde/verwijderde sets ook meetellen in het totaal.
  this.root.addEventListener('change', function (e) {
    if (e.target && e.target.matches && e.target.matches('.cfg-zakjes [data-blnc-zak]')) {
      self.recomputeZakjes(); self.render();
    }
  });
  // Signaal bij toevoegen/verwijderen van een set (geen [data-blnc-zak]-change beschikbaar).
  this.root.addEventListener('blnc:zakjes-changed', function () { self.recomputeZakjes(); self.render(); });

  this.root.querySelectorAll('[data-blnc-required]').forEach(function (inp) {
    inp.addEventListener('change', function () {
      if (inp.value) {
        var setEl = inp.closest('.cfg-zakjes__set');
        if (setEl) setEl.classList.remove('is-invalid');
        var errEl = self.root.querySelector('[data-blnc-error]');
        if (errEl) errEl.hidden = true;
      }
    });
  });

  this.root.querySelectorAll('.blnc-cfg__head').forEach(function (head) {
    head.addEventListener('click', function () {
      var row = head.closest('.blnc-cfg__row');
      var isOpen = row.getAttribute('data-open') === 'true';
      self.closeAll();
      if (!isOpen) {
        self.openRow(row);
        /* Ook meescrollen bij een klik op de kop. De Verder-knop deed dat al;
           een directe klik niet, en dan sprong de pagina: het vorige paneel
           klapt dicht, de pagina krimpt en de aangeklikte laag belandt ergens
           anders dan waar de klant net keek. Gemeld door Tjarco 8-8-2026. */
        self.scrollToRow(row);
      }
    });
  });

  this.root.querySelectorAll('[data-blnc-next]').forEach(function (b) {
    b.addEventListener('click', function () {
      var row = b.closest('.blnc-cfg__row');
      self.closeAll();
      var next = self.nextRow(row);
      if (next) { self.openRow(next); self.scrollToRow(next); }
    });
  });

  var first = this.root.querySelector('.blnc-cfg__row');
  if (first) this.openRow(first);

  if (this.qtyInput) this.qtyInput.addEventListener('input', function () { self.render(); });

  if (this.atcBtn) this.atcBtn.addEventListener('click', function () { self.addToCart(); });

  if (this.fileInput) {
    this.fileInput.addEventListener('change', function () {
      var nameEl = self.root.querySelector('[data-blnc-file-name]');
      if (nameEl && self.fileInput.files && self.fileInput.files.length) {
        nameEl.textContent = self.fileInput.files[0].name;
      }
    });
  }

  this.render();
  this.volgHandmatigeCategoriekeuze();
  this.updateMeestGekozen();
};

/*
 * Zet de prijskop, de "bespaar"-badge en de variantgebonden specificatierijen
 * gelijk aan de gekozen variant.
 *
 * Waarom dit bestaat: tot 8-8-2026 werkte een variantwissel alleen het totaal in
 * de koopbalk bij. Koos je 15mm, dan stond er tegelijk "Totaal EUR 249" en
 * bovenaan "EUR 149 / EUR 209 / BESPAAR EUR 60", plus "Board dikte: 10mm" en
 * artikelnummer 103080102 -- en 103 is volgens de eigen SKU-systematiek 10mm.
 * Vier tegenstrijdige uitspraken over hetzelfde artikel, op het moment van
 * betalen. Wie het artikelnummer doorgeeft aan een collega geeft het verkeerde
 * door; dat komt terug als supportticket of retour.
 *
 * De tabel wordt op naam van de rij gezocht en niet op positie: de
 * spec_labels-lijst verschilt per template, dus een index zou stil verschuiven.
 * Vindt hij een rij niet, dan gebeurt er niets -- liever een rij die niet
 * meebeweegt dan een verkeerde rij die wel verandert.
 */
/*
 * Het money-filter levert in deze winkel geen kale tekst maar HTML: Transcy
 * wikkelt elk bedrag in <span class="transcy-money">. Zet je dat rechtstreeks
 * als textContent, dan staan de tags letterlijk op de pagina -- dat gebeurde op
 * 8-8-2026 live in de preview. Hier halen we de tekst eruit zonder de HTML te
 * injecteren, zodat het werkt met en zonder die app.
 */
function moneyTekst(html) {
  if (!html) return '';
  var hulp = document.createElement('div');
  hulp.innerHTML = html;
  return (hulp.textContent || '').trim();
}

Configurator.prototype.syncVariantInfo = function (btn) {
  var money = moneyTekst(btn.getAttribute('data-price-money'));
  var compareMoney = moneyTekst(btn.getAttribute('data-compare-money'));
  var compare = readNum(btn, 'data-compare-at');
  var prijs = readNum(btn, 'data-price');

  var lijst = document.querySelector('[data-product-price-list]');
  if (lijst && money) {
    var hoofd = lijst.querySelector('.price:not(.price--compare) .transcy-money, .price:not(.price--compare)');
    if (hoofd) hoofd.textContent = money;
    var verg = lijst.querySelector('.price--compare');
    if (verg) {
      var toon = compare > prijs;
      verg.hidden = !toon;
      verg.style.display = toon ? '' : 'none';
      var vergTekst = verg.querySelector('.transcy-money') || verg;
      if (toon && compareMoney) vergTekst.textContent = compareMoney;
    }
  }

  /* De bespaar-badge hoort weg zodra er niets meer te besparen valt. Hem laten
     staan met een bedrag dat niet meer klopt is erger dan geen badge. */
  var badges = document.querySelector('[data-product-label-list]');
  if (badges) {
    var toonBadge = compare > prijs;
    badges.style.display = toonBadge ? '' : 'none';
    /* Verbergen alleen was niet genoeg: bij 12mm bleef er "BESPAAR EUR 60,00"
       staan terwijl het verschil EUR 10,00 is -- het bedrag van de 10mm.
       Een badge met het verkeerde bedrag is erger dan geen badge. */
    if (toonBadge) {
      var saving = moneyTekst(btn.getAttribute('data-saving-money'));
      var bedragEl = badges.querySelector('.transcy-money');
      if (bedragEl && saving) {
        bedragEl.textContent = saving;
      } else if (saving) {
        var label = badges.querySelector('.label');
        if (label) label.textContent = label.textContent.replace(/[\d.,]+\s*$/, '').trim() + ' ' + saving;
      }
    }
  }

  /* De balk toont de normale prijs van de gekozen variant, niet die van de
     variant waarmee de pagina laadde. */
  var vergEl2 = document.querySelector('[data-blnc-vergelijk]');
  if (vergEl2) {
    vergEl2.setAttribute('data-compare', String(compare));
    vergEl2.setAttribute('data-prijs', String(prijs));
    /* Het bedrag zelf wordt niet hier gezet maar in render(): dat is het enige
       punt dat ook de extra's kent, en de doorstreping vergelijkt het hele
       mandje. Twee schrijvers op hetzelfde element is bovendien vragen om een
       verschil tussen wat er staat en wat er gerekend wordt. */
  }

  var dikte = btn.getAttribute('data-label') || '';
  var sku = btn.getAttribute('data-sku') || '';
  document.querySelectorAll('.product-tabs__specification-item--name').forEach(function (naamEl) {
    var waardeEl = naamEl.nextElementSibling;
    if (!waardeEl) return;
    var naam = (naamEl.textContent || '').toLowerCase();
    if (naam.indexOf('dikte') > -1 && dikte) waardeEl.textContent = dikte;
    if (naam.indexOf('artikelnummer') > -1 && sku) waardeEl.textContent = sku;
  });
};

Configurator.prototype.selectVariant = function (btn, updateMedia) {
  this.root.querySelectorAll('.blnc-cfg__variant').forEach(function (b) { b.classList.remove('is-sel'); });
  btn.classList.add('is-sel');
  this.state.variantId = readNum(btn, 'data-variant-id');
  this.state.variantPrice = readNum(btn, 'data-price');
  this.syncVariantInfo(btn);
  this.setHeadValue(btn, btn.getAttribute('data-label') || '');
  if (updateMedia) this.switchGalleryMedia(btn.getAttribute('data-media-id'), btn.getAttribute('data-image'));
  this.updateMeestGekozen();
  this.render();
};

/*
 * Verplaatst de "meest gekozen"-badge naar de categorie die bij de boarddikte van
 * de gekozen variant hoort, en selecteert die categorie voor — maar alleen zolang
 * de klant nog niet zelf een categorie heeft aangeklikt. Wie bewust Comp kiest en
 * daarna van dikte wisselt, moet niet terug naar Premium gegooid worden.
 */
Configurator.prototype.updateMeestGekozen = function () {
  var selBtn = this.root.querySelector('.blnc-cfg__variant.is-sel');
  var variantTitel = selBtn ? (selBtn.getAttribute('data-label') || '') : '';
  var tekst = variantTitel + ' ' + (this.root.getAttribute('data-product-title') || '');
  var eigenDesign = this.root.getAttribute('data-eigen-design') === 'true';

  this.root.querySelectorAll('.cfg-zakjes__set').forEach(function (setEl) {
    var knoppen = Array.prototype.slice.call(setEl.querySelectorAll('.cfg-zakjes__cat-btn'));
    if (!knoppen.length) return;
    var cats = knoppen.map(function (b) { return b.getAttribute('data-cat'); });
    var doel = bepaalMeestGekozen(tekst, cats, eigenDesign);

    /*
     * Niet automatisch landen op een categorie die vrijwel leeg is. Gemeten
     * 8-8-2026: kies 15mm en de aanbeveling sprong naar Premium, waar 5 van de
     * 7 kleuren uitverkocht waren. De klant waardeert 100 euro op en komt uit
     * op een muur grijze vakjes -- het aanbevolen pad liep dood.
     *
     * De badge verhuist mee met de selectie. Anders wijst "meest gekozen" naar
     * iets anders dan waar de klant staat, en dan klopt de claim helemaal niet
     * meer.
     */
    function opVoorraad(b) {
      var op = parseInt(b.getAttribute('data-op') || '0', 10);
      var tot = parseInt(b.getAttribute('data-totaal') || '0', 10);
      return { op: op, tot: tot, ruim: tot === 0 || op * 2 >= tot };
    }
    /*
     * De uitwijk kiest alleen uit categorieën die überhaupt aanbevolen mógen
     * worden. Zonder die filter landde de badge steevast op REC: dat is de
     * grootste en altijd best gevulde categorie, dus hij won elke telling.
     * Tjarco zag dat op 8-8-2026 op de Vintage-set -- en het gold ook op het
     * vlaggenschip. REC is de zak die standaard al bij de set zit; hem
     * aanbevelen verkoopt niets en holt het label uit.
     */
    var MAG = ['premium', 'slide', 'comp', 'pro'];
    var doelBtn = knoppen.filter(function (b) { return b.getAttribute('data-cat') === doel; })[0];
    if (doelBtn && !opVoorraad(doelBtn).ruim) {
      var kandidaten = knoppen.filter(function (b) { return MAG.indexOf(b.getAttribute('data-cat')) > -1; });
      var beste = kandidaten.sort(function (a, b) { return opVoorraad(b).op - opVoorraad(a).op; })[0];
      if (beste && opVoorraad(beste).op > opVoorraad(doelBtn).op) {
        doel = beste.getAttribute('data-cat');
      }
    }

    knoppen.forEach(function (b) {
      var badge = b.querySelector('.cfg-zakjes__cat-badge');
      if (badge) badge.hidden = b.getAttribute('data-cat') !== doel;
    });

    if (!doel || setEl.dataset.blncUserPicked === '1') return;
    knoppen.forEach(function (b) {
      if (b.getAttribute('data-cat') === doel && !b.classList.contains('is-active')) b.click();
    });
  });
};

/*
 * Onthoudt of de klant zélf een zakjes-categorie koos. isTrusted is false bij een
 * programmatische .click(), dus onze eigen voorselectie telt hier niet mee.
 */
Configurator.prototype.volgHandmatigeCategoriekeuze = function () {
  this.root.addEventListener('click', function (e) {
    if (!e.isTrusted) return;
    var btn = e.target.closest && e.target.closest('.cfg-zakjes__cat-btn');
    if (!btn) return;
    var setEl = btn.closest('.cfg-zakjes__set');
    if (setEl) setEl.dataset.blncUserPicked = '1';
  }, true);
};

Configurator.prototype.switchGalleryMedia = function (mediaId, imgUrl) {
  // Focal main-product gallery: klik de nav-knop met dit media-id (triggert de eigen slider)
  if (mediaId) {
    var navBtn = document.querySelector('[data-action="select"][data-media-id="' + mediaId + '"]');
    if (navBtn) { navBtn.click(); return; }
  }
  // Horizon <media-gallery>: haal de gallery-sectie voor deze variant op (section render) en vervang 'm.
  var gallery = document.querySelector('media-gallery');
  if (gallery && this.state.variantId) {
    var section = gallery.closest('[id^="shopify-section-"]');
    var sectionId = section ? section.id.replace('shopify-section-', '') : null;
    if (sectionId) {
      var vid = this.state.variantId;
      fetch(location.pathname + '?variant=' + vid + '&section_id=' + sectionId, { headers: { Accept: 'text/html' } })
        .then(function (r) { return r.text(); })
        .then(function (html) {
          var fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('media-gallery');
          var cur = document.querySelector('media-gallery');
          if (fresh && cur) cur.replaceWith(fresh);
        })
        .catch(function () {});
      return;
    }
  }
  // Fallback: eigen media-afbeelding (indien aanwezig)
  if (imgUrl) {
    var main = document.querySelector('[data-blnc-media-main]');
    if (main) main.setAttribute('src', imgUrl);
  }
};

Configurator.prototype.applyChoice = function (radio, doRender) {
  var row = radio.closest('.blnc-cfg__row');
  var key = row.getAttribute('data-layer');
  this.state.choices[key] = {
    id: readNum(radio, 'data-variant-id') || null,
    price: readNum(radio, 'data-price'),
    label: radio.getAttribute('data-label') || ''
  };
  this.setHeadValue(radio, radio.getAttribute('data-label') || '');
  if (doRender) this.render();
};

Configurator.prototype.recomputeAddons = function () {
  var ups = [];
  /*
   * De expres-checkbox staat in een <label class="blnc-cfg__addon"> binnen
   * <div class="blnc-cfg__express"> (snippets/cfg-cornhole-express.liquid). Zonder
   * deze uitzondering matcht hij op beide selectors en telt hij dus dubbel: één keer
   * als upsell en één keer als express. Dat leverde een te hoge prijs in de
   * configurator én twee expresregels in de winkelwagen.
   */
  this.root.querySelectorAll('.blnc-cfg__addon input[type="checkbox"]:checked').forEach(function (cb) {
    if (cb.closest('.blnc-cfg__express')) return;
    ups.push({ id: readNum(cb, 'data-variant-id'), price: readNum(cb, 'data-price'), label: cb.getAttribute('data-label') || '' });
  });
  this.state.upsells = ups;
  var exp = this.root.querySelector('.blnc-cfg__express input[type="checkbox"]:checked');
  this.state.express = exp
    ? { id: readNum(exp, 'data-variant-id'), price: readNum(exp, 'data-price'), label: exp.getAttribute('data-label') || '' }
    : null;
  this.updateAddonHeads();
};

// Toon gekozen labels van checkbox-lagen (afwerking / accessoires+opties) in de rij-kop
Configurator.prototype.updateAddonHeads = function () {
  this.root.querySelectorAll('.blnc-cfg__row').forEach(function (row) {
    if (!row.querySelector('.blnc-cfg__addon input[type="checkbox"]')) return;
    var labels = [];
    row.querySelectorAll('.blnc-cfg__addon input[type="checkbox"]:checked').forEach(function (cb) {
      labels.push(cb.getAttribute('data-label') || '');
    });
    var val = row.querySelector('[data-blnc-value]');
    if (val) val.textContent = labels.join(', ');
  });
};

Configurator.prototype.recomputeZakjes = function () {
  var zak = [];
  this.root.querySelectorAll('.cfg-zakjes [data-blnc-zak]').forEach(function (inp) {
    if (inp.value) {
      zak.push({
        id: parseInt(inp.value, 10),
        price: parseInt(inp.getAttribute('data-price'), 10) || 0,
        label: inp.getAttribute('data-label') || '',
        category: inp.getAttribute('data-category') || '',
        required: inp.hasAttribute('data-blnc-required')
      });
    }
  });
  this.state.zakjes = zak;
  // Toon aantal gekozen sets in de rij-kop
  var row = this.root.querySelector('.blnc-cfg__row[data-layer="zakjes"]');
  if (row) {
    var val = row.querySelector('[data-blnc-value]');
    if (val) val.textContent = zak.length ? (zak.length + ' set' + (zak.length > 1 ? 's' : '')) : '';
  }
};

Configurator.prototype.setHeadValue = function (el, value) {
  var row = el.closest('.blnc-cfg__row');
  if (!row) return;
  var valEl = row.querySelector('[data-blnc-value]');
  if (valEl) valEl.textContent = value;
};

Configurator.prototype.swapMedia = function (src) {
  if (!src) return;
  var main = this.root.querySelector('[data-blnc-media-main]');
  if (main) main.setAttribute('src', src);
};

Configurator.prototype.openRow = function (row) {
  row.setAttribute('data-open', 'true');
  var head = row.querySelector('.blnc-cfg__head');
  if (head) head.setAttribute('aria-expanded', 'true');
  var body = row.querySelector('.blnc-cfg__body');
  if (body) body.style.display = 'block';
};

Configurator.prototype.closeAll = function () {
  this.root.querySelectorAll('.blnc-cfg__row').forEach(function (row) {
    row.setAttribute('data-open', 'false');
    var head = row.querySelector('.blnc-cfg__head');
    if (head) head.setAttribute('aria-expanded', 'false');
    var body = row.querySelector('.blnc-cfg__body');
    if (body) body.style.display = 'none';
  });
};

Configurator.prototype.nextRow = function (row) {
  var rows = Array.prototype.slice.call(this.root.querySelectorAll('.blnc-cfg__row'));
  var idx = rows.indexOf(row);
  return idx > -1 && idx < rows.length - 1 ? rows[idx + 1] : null;
};

Configurator.prototype.advanceFrom = function (el) {
  var row = el.closest('.blnc-cfg__row');
  if (!row || row.getAttribute('data-advance') !== 'true') return;
  var self = this;
  row.classList.add('is-just-chosen');
  setTimeout(function () {
    row.classList.remove('is-just-chosen');
    self.closeAll();
    var next = self.nextRow(row);
    if (next) { self.openRow(next); self.scrollToRow(next); }
  }, 260);
};

// Scroll de zojuist geopende laag in beeld (scroll-margin-top in CSS houdt 'm onder de sticky header).
Configurator.prototype.scrollToRow = function (row) {
  if (!row || !row.scrollIntoView) return;
  setTimeout(function () { row.scrollIntoView({ block: 'start' }); }, 40);
};

/*
 * Het aantalveld accepteerde -5: min="1" wordt nooit afgedwongen omdat de knop
 * type="button" is en er dus geen formuliervalidatie langskomt. Het totaal bleef
 * dan stil op het oude bedrag staan terwijl er iets anders in het veld stond.
 * Gemeten 8-8-2026. Nu wordt de waarde teruggeschreven, zodat wat de klant ziet
 * en wat we rekenen hetzelfde zijn.
 */
Configurator.prototype.getQty = function () {
  if (!this.qtyInput) return 1;
  var q = parseInt(this.qtyInput.value, 10);
  var geldig = (isNaN(q) || q < 1) ? 1 : q;
  if (String(geldig) !== this.qtyInput.value) this.qtyInput.value = geldig;
  return geldig;
};

Configurator.prototype.render = function () {
  var perStuk = computeTotal(this.state);
  var qty = this.getQty();
  var totaal = perStuk * qty;
  if (this.totalEl) this.totalEl.textContent = formatMoney(totaal);

  /* De doorgestreepte prijs verdween zodra de klant zakjes of een extra koos.
     Dat was mijn eerste oplossing voor een echt probleem -- EUR 209 doorgestreept
     naast EUR 168,50 vergelijkt twee verschillende dingen -- maar het gevolg was
     dat de korting precies verdween op het moment dat de klant meer ging kopen.

     De vergelijking groeit nu mee. De korting zit op de set, niet op de extra's,
     dus wat dezelfde samenstelling zonder setkorting zou kosten is:
     normale setprijs + diezelfde extra's. Bij 149 (normaal 209) plus 19,50 aan
     zakjes staat er dus 228,50 doorgestreept naast 168,50. Dat is hetzelfde
     mandje tegen de normale prijs -- geen opgeblazen ankerprijs.

     De extra's tellen tegen hun eigen prijs mee, zonder eigen doorstreping. We
     claimen dus geen korting op de zakjes die er niet is; het bespaarde bedrag
     blijft de setkorting en verandert niet als de klant meer toevoegt. */
  var vergEl = document.querySelector('[data-blnc-vergelijk]');
  if (vergEl) {
    var compare = parseInt(vergEl.getAttribute('data-compare') || '0', 10);
    var basis = parseInt(vergEl.getAttribute('data-prijs') || '0', 10);
    var korting = compare > basis ? compare - basis : 0;
    vergEl.hidden = korting === 0;
    if (korting > 0) {
      var bedragEl = vergEl.querySelector('[data-blnc-vergelijk-bedrag]');
      if (bedragEl) bedragEl.textContent = formatMoney(totaal + korting * qty);
      var bespaarEl = vergEl.querySelector('[data-blnc-bespaar]');
      if (bespaarEl) {
        var sjabloon = bespaarEl.getAttribute('data-sjabloon') || '';
        bespaarEl.textContent = sjabloon.replace('%bedrag%', formatMoney(korting * qty));
      }
    }
  }
};

// Custom-zakjes-sets: eigen cart-regel met properties[Design] = de per-set upload.
Configurator.prototype.customBagSets = function () {
  var out = [];
  this.root.querySelectorAll('.cfg-zakjes__set').forEach(function (setEl) {
    var inp = setEl.querySelector('[data-blnc-zak]');
    if (!inp || !inp.value) return;
    if (inp.getAttribute('data-category') !== 'custom') return;
    var file = setEl.querySelector('[data-blnc-zak-file]');
    out.push({
      id: parseInt(inp.value, 10),
      required: inp.hasAttribute('data-blnc-required'),
      file: (file && file.files && file.files.length) ? file.files[0] : null
    });
  });
  return out;
};

Configurator.prototype.addToCart = function () {
  var self = this;
  if (!this.state.variantId) return;

  // Verplichte velden (bv. eerste zakjes-set)
  var invalid = [];
  this.root.querySelectorAll('[data-blnc-required]').forEach(function (inp) {
    if (!inp.value) {
      var setEl = inp.closest('.cfg-zakjes__set');
      if (setEl) setEl.classList.add('is-invalid');
      invalid.push(inp);
    }
  });
  // Custom-upload is optioneel (design mag ook later aangeleverd worden) → geen bestand-validatie.
  if (invalid.length) {
    var firstInvalid = invalid[0];
    var row = firstInvalid.closest('.blnc-cfg__row');
    if (row) { this.closeAll(); this.openRow(row); }
    var errEl = this.root.querySelector('[data-blnc-error]');
    if (errEl && this.atcBtn) { errEl.textContent = this.atcBtn.getAttribute('data-required-msg') || ''; errEl.hidden = false; }
    // Scroll naar de eerste ontbrekende (rode) set, zodat de klant ziet wat er nog moet
    var target = firstInvalid.closest('.cfg-zakjes__set') || row;
    if (target && target.scrollIntoView) {
      setTimeout(function () { target.scrollIntoView({ block: 'center' }); }, 90);
    }
    return;
  }

  var btn = this.atcBtn;
  var original = btn ? btn.textContent : '';
  var loadingText = (btn && btn.getAttribute('data-loading')) || 'Even geduld…';
  if (btn) { btn.disabled = true; btn.textContent = loadingText; }

  var qty = this.getQty();

  /*
   * uid wordt hier eenmalig vastgelegd en this.uid meteen synchroon ververst — vóór
   * enige netwerkoproep, ongeacht of die straks slaagt of faalt. Zo dragen alle
   * regels van déze add-to-cart (hoofdproduct, custom-sets, add-on-batch) altijd
   * dezelfde uid, en krijgt een eventuele herhaalpoging na een mislukte add automatisch
   * een verse groep in plaats van samen te smelten met de half-toegevoegde vorige.
   */
  var uid = this.uid;
  this.uid = maakUid(this.baseUid);

  // Hoofdproduct (board) + eventueel board-design als multipart-add.
  var mainFd = new FormData();
  mainFd.append('id', String(this.state.variantId));
  mainFd.append('quantity', String(qty));
  var props = bouwHoofdProps(this.state, uid);
  Object.keys(props).forEach(function (k) { mainFd.append('properties[' + k + ']', props[k]); });
  // this.state is live (recomputeZakjes/recomputeAddons herschrijven 'm bij elke DOM-wijziging, en
  // tijdens de add blijven de zakjes-knoppen bedienbaar) — daarom hier al vastgelegd, net als uid en
  // qty, in plaats van pas in de .then() na de netwerkronde te lezen.
  var addonItems = bouwAddonItems(this.state, uid, qty);
  if (this.fileInput && this.fileInput.files && this.fileInput.files.length) {
    mainFd.append('properties[Design]', this.fileInput.files[0]);
  }

  // Elke custom-zakjes-set: eigen multipart-add met properties[Design] = die set z'n upload.
  var customForms = this.customBagSets().map(function (cs) {
    var fd = new FormData();
    fd.append('id', String(cs.id));
    fd.append('quantity', String(qty));
    fd.append('properties[_config]', uid);
    fd.append('properties[_cfg_role]', cs.required ? 'locked' : 'addon');
    if (cs.file) fd.append('properties[Design]', cs.file);
    return fd;
  });

  function postForm(fd) {
    return fetch('/cart/add.js', { method: 'POST', headers: { 'Accept': 'application/json' }, body: fd })
      .then(function (r) { return r.json(); })
      .then(function (res) { if (res && res.status && res.status !== 200) throw new Error(res.description || 'cart error'); return res; });
  }

  function postJson(items) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: items })
    })
      .then(function (r) { return r.json(); })
      .then(function (res) { if (res && res.status && res.status !== 200) throw new Error(res.description || 'cart error'); return res; });
  }

  // Sequentieel: hoofdproduct → custom-sets (met bestand) → overige items (JSON).
  var chain = postForm(mainFd);
  customForms.forEach(function (fd) { chain = chain.then(function () { return postForm(fd); }); });
  chain
    .then(function () { return addonItems.length ? postJson(addonItems) : null; })
    .then(function () {
      if (btn) { btn.disabled = false; btn.textContent = original; }
      self.openCart();
    })
    .catch(function (err) {
      console.warn('[cfg] cart error:', err);
      if (btn) { btn.disabled = false; btn.textContent = original; }
      var errEl = self.root.querySelector('[data-blnc-error]');
      if (errEl && btn) { errEl.textContent = btn.getAttribute('data-cart-error-msg') || ''; errEl.hidden = false; }
    });
};

Configurator.prototype.openCart = function () {
  if (this.cartMode === 'minicart') { this.openMiniCart(); return; }
  // Horizon-drawer (BLNC)
  var cartBtn = document.querySelector('[aria-controls="cart-drawer"], [data-target="cart"]');
  if (cartBtn) { cartBtn.click(); return; }
  document.dispatchEvent(new CustomEvent('cart:open'));
  window.dispatchEvent(new CustomEvent('cart:refresh'));
  setTimeout(function () {
    var drawer = document.querySelector('cart-drawer, #cart-drawer');
    if (!drawer) window.location.href = '/cart';
  }, 800);
};

Configurator.prototype.openMiniCart = function () {
  // WW Focal-thema: mini-cart section verversen en openen
  var miniCart = document.getElementById('mini-cart');
  var sectionId = miniCart && miniCart.closest('[id^="shopify-section-"]')
    ? miniCart.closest('[id^="shopify-section-"]').id.replace('shopify-section-', '')
    : 'mini-cart';
  fetch('/?section_id=' + sectionId, { headers: { 'Accept': 'text/html' } })
    .then(function (r) { return r.text(); })
    .then(function (html) {
      var mc = document.getElementById('mini-cart');
      if (mc) {
        var temp = document.createElement('div');
        temp.innerHTML = html;
        var fresh = temp.querySelector('#mini-cart');
        if (fresh) mc.innerHTML = fresh.innerHTML;
      }
      var cartBtn = document.querySelector('[aria-controls="mini-cart"]');
      if (cartBtn) { cartBtn.click(); return; }
      var notif = document.querySelector('cart-notification');
      if (notif) { notif.hidden = false; return; }
      window.location.href = '/cart';
    })
    .catch(function () { window.location.href = '/cart'; });
};

function initAll() {
  document.querySelectorAll('#blnc-cfg, .blnc-cfg[data-uid]').forEach(function (root) {
    if (root.dataset.blncInit) return;
    root.dataset.blncInit = '1';
    new Configurator(root);
  });
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
}
